import React, { useState, useEffect, useCallback } from 'react';
import ragService from '../../services/rag.service';
import { useAuthStore } from '../../store/authStore';
import ConfirmDeleteDialog from '../../components/common/ConfirmDeleteDialog';

const DataCleanup = () => {
  const { user } = useAuthStore();
  const isSuperadmin = user?.role === 'superadmin' || user?.is_superuser;

  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [deletingItems, setDeletingItems] = useState(new Set());
  const [activeTab, setActiveTab] = useState('orphan_vector');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await ragService.getDataCleanupReport();
      setReport(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load cleanup report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperadmin) loadReport();
  }, [isSuperadmin, loadReport]);

  const openDeleteDialog = (type, id, name) => {
    setItemToDelete({ type, id, name });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setDeleteDialogOpen(false);
    const { type, id, name } = itemToDelete;

    if (type === 'vector') {
      setDeletingItems(prev => new Set(prev).add(`vec_${name}`));
      setSuccessMessage('');
      try {
        const result = await ragService.deleteOrphanVectorCollection(name);
        setSuccessMessage(result.message);
        await loadReport();
      } catch (err) {
        setError(err.response?.data?.detail || 'Delete failed');
      } finally {
        setDeletingItems(prev => { const s = new Set(prev); s.delete(`vec_${name}`); return s; });
      }
    } else {
      setDeletingItems(prev => new Set(prev).add(`tc_${id}`));
      setSuccessMessage('');
      try {
        const result = await ragService.deleteOrphanTenantCollection(id);
        setSuccessMessage(result.message);
        await loadReport();
      } catch (err) {
        setError(err.response?.data?.detail || 'Delete failed');
      } finally {
        setDeletingItems(prev => { const s = new Set(prev); s.delete(`tc_${id}`); return s; });
      }
    }
    setItemToDelete(null);
  };

  const handleSyncCounts = async () => {
    setSyncing(true);
    setSuccessMessage('');
    setError('');
    try {
      const result = await ragService.syncCollectionCounts();
      setSuccessMessage(`${result.message} (${result.updated}/${result.total_checked} updated)`);
      await loadReport();
    } catch (err) {
      setError(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  if (!isSuperadmin) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-300 font-medium">Access denied. Superadmin only.</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: 'orphan_vector', label: 'Orphan Vector Collections', count: report?.summary?.orphan_vector || 0, color: 'red' },
    { key: 'empty', label: 'Empty Collections', count: report?.summary?.empty || 0, color: 'yellow' },
    { key: 'orphan_tenant', label: 'Orphan DB Records', count: report?.summary?.orphan_tenant || 0, color: 'orange' },
    { key: 'mismatched', label: 'Mismatched Counts', count: report?.summary?.mismatched || 0, color: 'blue' },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Data Cleanup</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Find and remove orphan collections, empty collections, and fix mismatched counts
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleSyncCounts}
            disabled={syncing || loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
          >
            {syncing ? 'Syncing...' : '🔄 Sync Counts'}
          </button>
          <button
            onClick={loadReport}
            disabled={loading}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 text-sm font-medium"
          >
            {loading ? 'Loading...' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
        </div>
      )}
      {successMessage && (
        <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
          <p className="text-green-700 dark:text-green-300 text-sm">{successMessage}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-500">Scanning collections...</span>
        </div>
      ) : report ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <SummaryCard label="Tenant Collections" value={report.summary.total_tenant_collections} color="gray" />
            <SummaryCard label="Vector Collections" value={report.summary.total_vector_collections} color="gray" />
            <SummaryCard label="Orphans" value={report.summary.orphan_vector + report.summary.orphan_tenant} color="red" />
            <SummaryCard label="Mismatched" value={report.summary.mismatched} color="blue" />
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
            <nav className="flex -mb-px space-x-6">
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  {tab.label}
                  {tab.count > 0 && (
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold bg-${tab.color}-100 text-${tab.color}-700 dark:bg-${tab.color}-900/30 dark:text-${tab.color}-300`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'orphan_vector' && (
            <CollectionTable
              title="Orphan Vector Collections"
              description="Collections in the vector DB with no matching tenant_collections record. Safe to delete."
              items={report.orphan_vector_collections}
              columns={[
                { key: 'collection_name', label: 'Collection Name' },
                { key: 'chunk_count', label: 'Chunks', align: 'right' },
              ]}
              emptyMessage="No orphan vector collections found"
              renderActions={(item) => (
                <button
                  onClick={() => openDeleteDialog('vector', null, item.collection_name)}
                  disabled={deletingItems.has(`vec_${item.collection_name}`)}
                  className="px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 disabled:opacity-50"
                >
                  {deletingItems.has(`vec_${item.collection_name}`) ? 'Deleting...' : 'Delete'}
                </button>
              )}
            />
          )}

          {activeTab === 'empty' && (
            <CollectionTable
              title="Empty Collections"
              description="Collections with 0 chunks in both DB record and vector DB."
              items={report.empty_collections}
              columns={[
                { key: 'internal_name', label: 'Internal Name' },
                { key: 'name', label: 'Display Name' },
                { key: 'tenant_name', label: 'Tenant' },
                { key: 'chunk_count', label: 'Chunks', align: 'right' },
              ]}
              emptyMessage="No empty collections found"
              renderActions={(item) => (
                <button
                  onClick={() => openDeleteDialog('tenant', item.id, item.internal_name)}
                  disabled={deletingItems.has(`tc_${item.id}`)}
                  className="px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 disabled:opacity-50"
                >
                  {deletingItems.has(`tc_${item.id}`) ? 'Deleting...' : 'Delete'}
                </button>
              )}
            />
          )}

          {activeTab === 'orphan_tenant' && (
            <CollectionTable
              title="Orphan DB Records"
              description="Records in tenant_collections with no matching vector DB collection."
              items={report.orphan_tenant_records}
              columns={[
                { key: 'internal_name', label: 'Internal Name' },
                { key: 'name', label: 'Display Name' },
                { key: 'tenant_name', label: 'Tenant' },
                { key: 'chunk_count', label: 'Recorded Chunks', align: 'right' },
              ]}
              emptyMessage="No orphan DB records found"
              renderActions={(item) => (
                <button
                  onClick={() => openDeleteDialog('tenant', item.id, item.internal_name)}
                  disabled={deletingItems.has(`tc_${item.id}`)}
                  className="px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 disabled:opacity-50"
                >
                  {deletingItems.has(`tc_${item.id}`) ? 'Deleting...' : 'Delete Record'}
                </button>
              )}
            />
          )}

          {activeTab === 'mismatched' && (
            <CollectionTable
              title="Mismatched Counts"
              description="Collections where recorded chunk_count differs from actual vector DB count. Use 'Sync Counts' to fix."
              items={report.mismatched_counts}
              columns={[
                { key: 'internal_name', label: 'Internal Name' },
                { key: 'name', label: 'Display Name' },
                { key: 'tenant_name', label: 'Tenant' },
                { key: 'recorded_chunks', label: 'Recorded', align: 'right' },
                { key: 'actual_chunks', label: 'Actual', align: 'right' },
              ]}
              emptyMessage="All counts are in sync"
              renderActions={() => (
                <span className="text-xs text-gray-400">Use Sync Counts ↑</span>
              )}
            />
          )}
        </>
      ) : null}

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setItemToDelete(null); }}
        onConfirm={handleConfirmDelete}
        title={itemToDelete?.type === 'vector' ? 'Delete Vector Collection' : 'Delete Collection Record'}
        entityName={itemToDelete?.name || ''}
        warningText="This will permanently delete this collection and all its data. This action cannot be undone."
        confirmButtonText="Delete"
      />
    </div>
  );
};

// Reusable summary card
const SummaryCard = ({ label, value, color }) => (
  <div className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4`}>
    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${
      color === 'red' && value > 0 ? 'text-red-600 dark:text-red-400' :
      color === 'blue' && value > 0 ? 'text-blue-600 dark:text-blue-400' :
      'text-gray-900 dark:text-white'
    }`}>
      {value}
    </p>
  </div>
);

// Reusable table component
const CollectionTable = ({ title, description, items, columns, emptyMessage, renderActions }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
      <h3 className="font-semibold text-gray-900 dark:text-white">{title}</h3>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{description}</p>
    </div>
    {items.length === 0 ? (
      <div className="p-8 text-center text-gray-400 dark:text-gray-500">
        <p>✅ {emptyMessage}</p>
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-750">
              {columns.map(col => (
                <th key={col.key} className={`px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase ${col.align === 'right' ? 'text-right' : 'text-left'}`}>
                  {col.label}
                </th>
              ))}
              <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {items.map((item, idx) => (
              <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                {columns.map(col => (
                  <td key={col.key} className={`px-4 py-3 ${col.align === 'right' ? 'text-right' : 'text-left'} text-gray-700 dark:text-gray-300`}>
                    {col.key === 'collection_name' || col.key === 'internal_name' ? (
                      <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded font-mono">{item[col.key]}</code>
                    ) : (
                      item[col.key] ?? '-'
                    )}
                  </td>
                ))}
                <td className="px-4 py-3 text-right">
                  {renderActions(item)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export default DataCleanup;
